import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useLocalStorage from '@hooks/useLocalStorage';
import { constant } from '@config/BaseSetting';
import { showFixedAlert } from '@features/application/applicationSlice';
import {
  useSyncM365IdMutation,
  useValidateM365IdMutation,
} from '@services/WebApiService/SyncM365IdService';
import ClientRoutes from '@config/Routes/WebClientRoutes';
import useForm from './syncM365Id/useForm';

export default function useSyncM365Id({ selectedAccount, onSuccessRequest }) {
  const dispatch = useDispatch();
  const form = useForm();
  const [accessTokenSet] = useLocalStorage(constant.ACCESS_TOKEN_STORAGE);
  const [syncM365IdMutation, { isLoading }] = useSyncM365IdMutation();
  const [validateM365IdMutation, { isLoading: isValidate }] = useValidateM365IdMutation();
  const [response, setResponse] = useState(null);
  const [m365IdList, setM365IdList] = useState([]);
  const { t } = useTranslation();

  const onLoad = async () => {
    try {
      const result = await validateM365IdMutation({
        accessToken: accessTokenSet,
        parameters: {
          'support-ticket': form.attributes.supportTicketAttributes.value,
          description: form.attributes.descriptionAttributes.value,
          'escalation-ticket': form.attributes.escalationTicketAttributes.value,
          'email-account-id': selectedAccount.id,
          'organization-owner-id': selectedAccount.organizationId,
          'm365-id': form.attributes.m365IdAttributes.value,
        },
      }).unwrap();
      const m365Data =
        typeof result?.data?.message === 'string'
          ? JSON.parse(result.data.message)
          : result.data.message;
      setM365IdList(m365Data.map((item) => ({ value: item.Id, radioLabel: item.Id, data: item })));
    } catch (err) {
      setM365IdList([]);
    }
  };
  useEffect(() => {
    onLoad();
  }, [selectedAccount?.id]);
  const submitRequest = async () => {
    try {
      const result = await syncM365IdMutation({
        accessToken: accessTokenSet,
        parameters: {
          'support-ticket': form.attributes.supportTicketAttributes.value,
          description: form.attributes.descriptionAttributes.value,
          'escalation-ticket': form.attributes.escalationTicketAttributes.value,
          'email-account-id': selectedAccount.id,
          'organization-owner-id': selectedAccount.organizationId,
          'm365-id': form.attributes.m365IdAttributes.value,
        },
      }).unwrap();
      setResponse(result);
    } catch (err) {
      setResponse({ error: err.data });
    }
  };

  const onValidateData = (e) => {
    form.onValidateData(e);
  };

  useEffect(() => {
    if (form.isValid) {
      submitRequest();
    }
  }, [form.isValid]);

  useEffect(() => {
    if (response?.['status-code'] === 200) {
      form.resetForm();
      if (response?.data?.serviceTypeCode) {
        const link = ClientRoutes.toolsRequestStatusPath({
          'service-type': response?.data?.serviceTypeCode,
        });

        dispatch(
          showFixedAlert({
            descriptionHtml: t('user_backup_page.sync_m365_id.success_submitted_with_link', {
              link,
            }),
          })
        );
      } else {
        dispatch(
          showFixedAlert({
            descriptionHtml: t('user_backup_page.sync_m365_id.success_submitted'),
          })
        );
      }
      if (onSuccessRequest) onSuccessRequest();
    } else if (response?.error) {
      dispatch(
        showFixedAlert({
          descriptionHtml: response?.error?.message,
          severity: 'error',
        })
      );
    }
  }, [response]);

  return {
    form,
    onValidateData,
    objectState: { fetching: isLoading || isValidate },
    m365IdList,
  };
}
